import React from 'react';
import { Text, Link, Box } from '@chakra-ui/react';
import DOMPurify from 'dompurify';

/**
 * ParagraphComponent dynamically renders paragraphs with support for inline HTML tags and hyperlinks with modal functionality.
 * @param {boolean|string} show - When false (or "false"), the paragraph is not rendered. Defaults to true.
 * @param {string} content - The text content of the paragraph, can include HTML markup.
 * @param {string} variant - The typography variant (e.g., "p1", "p2").
 * @param {string} ariaLabel - Accessibility label for the paragraph.
 * @param {string} role - Accessibility role for the paragraph.
 * @param {Array} links - Array of link objects with modal functionality.
 * @param {Function} handleButtonAction - Callback to handle button actions (for modal links).
 */

const ParagraphComponent = ({
  show = true,
  content,
  variant,
  textAlign,
  marginBottom,
  ariaLabel,
  role = 'paragraph',
  color,
  links,
  handleButtonAction,
}) => {
  // show may arrive as a "true"/"false" string from resolved content placeholders
  if (String(show).toLowerCase() === 'false') return null;

  const sanitizedContent = DOMPurify.sanitize(content, {
    ADD_ATTR: ['target', 'rel']
  });

  // Render links if they exist
  const renderLinks = () => {
    if (!links || !Array.isArray(links)) return null;

    return (
      <Box mt={2}>
        {links.map((link, index) => {
          // Use link variant if provided, otherwise fall back to paragraph variant
          const linkVariant = link.variant || variant || 'body-md-min-reg';
          
          return (
            <Link
              key={index}
              style={{
                fontFamily: `var(--${linkVariant}-font-family, Roboto)`,
                fontWeight: `var(--${linkVariant}-font-weight, 400)`,
                fontSize: `var(--${linkVariant}-font-size, 18px)`,
                lineHeight: `var(--${linkVariant}-line-height, 150%)`,
                color: link.color || `var(--${linkVariant}-color, var(--sutter-sync-purple, #5F1B93))`,
                textDecoration: 'underline',
                textDecorationStyle: 'solid',
                textDecorationOffset: '0%',
                textDecorationThickness: '0%',
              }}
              cursor="pointer"
              onClick={() => {
                if (link.action === 'modal' && link.modalContent && handleButtonAction) {
                  handleButtonAction('modal', {
                    actionType: link.actionType || 'open',
                    modalContent: link.modalContent,
                    url: link.url,
                    deeplinkUrl: link.deeplinkUrl
                  });
                } else if (link.url && handleButtonAction) {
                  handleButtonAction('link', {
                    url: link.url,
                    deeplinkUrl: link.deeplinkUrl
                  });
                }
              }}
                _hover={{ 
                 color: link.hoverColor || `var(--${linkVariant}-hover-color, var(--sutter-sync-purple, #5F1B93))`,
                 textDecoration: 'underline'
               }}
              aria-label={link.ariaLabel || link.label}
              title={link.hoverText || link.tooltip || link.label}
            >
              {link.label}
            </Link>
          );
        })}
      </Box>
    );
  };

  return (
    <Box mb={marginBottom || 4}>
      <Text
        style={{
          fontFamily: `var(--${variant}-font-family, sans-serif)`,
          fontWeight: `var(--${variant}-font-weight, normal)`,
          fontSize: `var(--${variant}-font-size, 16px)`,
          lineHeight: `var(--${variant}-line-height, 1.5)`,
          color: color || `var(--${variant}-color, var(--sutter-sync-purple))`,
          textAlign: textAlign || `var(--${variant}-text-align, left)`,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        aria-label={ariaLabel}
        role={role}
      />
      {renderLinks()}
    </Box>
  );
};

export default ParagraphComponent;
